import {Component, DestroyRef, OnInit} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {Subscription, timer} from 'rxjs';

import {MatFormField, MatLabel} from "@angular/material/form-field";
import {FormControl, FormGroup, ReactiveFormsModule, Validators} from "@angular/forms";
import {MatInput, MatInputModule} from "@angular/material/input";
import {MatButton} from "@angular/material/button";
import {MatCard, MatCardContent} from "@angular/material/card";
import {MatProgressSpinnerModule} from "@angular/material/progress-spinner";
import {ActivatedRoute, RouterLink} from "@angular/router";
import {TranslocoModule, TranslocoService} from "@jsverse/transloco";
import {LoginService} from "./login.service";
import {EmailVerificationService} from "../../services/email-verification.service";
import {LoginResponse} from "./login.object";
import {HttpClient, HttpErrorResponse} from "@angular/common/http";
import {resolveApiBase} from "../../core/api-base";
import {TenantConfigService} from "../../services/tenant-config.service";
import {TenantI18nService} from "../../services/tenant-i18n.service";
import {CaptchaConfig, CaptchaService, CaptchaToken} from "../../services/captcha.service";
import {LanguageService} from "../../../../../shared/i18n/language.service";

// Backend error CODE → Transloco key (AC2). The login Lambda (backend/login/
// error.go) emits: invalid_grant (bad credentials), invalid_request,
// tenant_closed, server_error, captcha_failed, access_denied, account_locked
// (+ invalid_client/unauthorized elsewhere). Bad credentials — both an UNKNOWN
// user and a WRONG password — return `invalid_grant` with an identical
// message/status (anti-enumeration), mapped here to "invalid username or
// password".
const ERROR_KEYS: Record<string, string> = {
    captcha_failed: 'common.errors.captchaFailed',
    tenant_closed: 'login.errors.tenantClosed',
    access_denied: 'login.errors.accessDenied',
    invalid_grant: 'login.errors.invalidCredentials',
    invalid_request: 'common.errors.invalidRequest',
    server_error: 'common.errors.serverError',
    // Story 28.3 — the Application requires a verified email and this user is
    // unverified. Distinct state: shows a "Resend verification email" action.
    email_not_verified: 'login.errors.emailNotVerified',
    // Story 28.4 — account locked after too many failed attempts. The message
    // directs the user to reset their password (which clears the lockout); the
    // always-visible forgot-password link below is the action.
    account_locked: 'login.errors.accountLocked',
};

// How long the login card waits for the tenant config before rendering anyway.
// A warm openid-configuration Lambda answers in well under this; a cold one can
// take several seconds, so we cap the spinner rather than block the card (and a
// possible wrong-language flash) on the full cold-start latency. Story 29.4
// follow-up (operator-reported 5s cold-start flicker).
const CONFIG_SPINNER_TIMEOUT_MS = 2000;

@Component({
    selector: 'app-login',
    imports: [MatFormField, MatLabel, ReactiveFormsModule, MatInputModule, MatInput, MatButton, MatCard, MatCardContent, RouterLink, TranslocoModule, MatProgressSpinnerModule],
    templateUrl: './login.component.html',
    styleUrl: './login.component.css'
})
export class LoginComponent implements OnInit {
    loginForm: FormGroup;
    signupEnabled = false;
    captchaRequired = false;
    // Story 54.4 — which captcha discovery says to run, and whether it is
    // being solved right now (drives aria-busy + the polite status line).
    private captchaConfig: CaptchaConfig | undefined;
    captchaSolving = false;
    // True while the per-client tenant config is being fetched: the card holds
    // behind a spinner so it paints once (in the resolved language) instead of
    // flashing the browser-detected language then flipping when config arrives.
    // Bounded by CONFIG_SPINNER_TIMEOUT_MS so a cold Lambda never blanks the page.
    configLoading = false;
    // True while we check whether a custom sign-in page is published for this
    // client (Epic 30). If one is published we redirect to /login where
    // serve-pages returns the operator's HTML; the spinner prevents a flash of
    // the default form before we know which to show.
    customPageChecking = false;
    private configTimeoutSub?: Subscription;
    // Story 53.9 (code review): the on-load silent sign-in, so a credential
    // submit can cancel it — otherwise two postAuthRedirect()s race and the
    // last Set-Cookie decides which account /authorize continues as.
    private silentSignIn?: Subscription;
    submitting = false;
    private _redirectUri: string;
    private _clientId: string;
    private _error: string | null;
    private _resetSuccess = false;
    private _emailNotVerified = false;
    resendSent = false;
    resendInProgress = false;
    private _audience: string;
    private _state: string;
    private _scope: string;
    private _codeChallenge: string;
    private _nonce: string;
    // Story 32.4 — device sign-in round-trip. When a session-less /device visitor
    // is bounced here with ?device_user_code=XXXX, the post-auth redirect returns
    // to /device instead of building an /authorize URL (the device flow has no
    // OAuth redirect params). Strictly additive — the normal /authorize path is
    // untouched when this is absent.
    private _deviceUserCode: string;
    // Story 49.6 (EO-347) AC4b — the flow's correlation id. /authorize mints it
    // and hands it over on the /login redirect; it is posted back on /login so
    // every attempt of one flow correlates, and carried onto the rebuilt
    // /authorize so the flow resumes under the same id. A direct visit has none
    // until /login returns the one it minted.
    private _correlationId: string;

    constructor(private loginService: LoginService, private emailVerificationService: EmailVerificationService, private route: ActivatedRoute, private tenantConfigService: TenantConfigService, private tenantI18nService: TenantI18nService, private captchaService: CaptchaService, private translocoService: TranslocoService, private destroyRef: DestroyRef, private languageService: LanguageService, private http: HttpClient) {
        this.route.queryParams.subscribe(params => {
            this._redirectUri = params['redirect_uri'];
            this._audience = params['audience'];
            this._state = params['state'];
            this._scope = params['scope'];
            this._clientId = params['client_id'];
            this._error = params['error'];
            this._codeChallenge = params['code_challenge'];
            this._nonce = params['nonce'];
            this._deviceUserCode = params['device_user_code'];
            this._correlationId = params['correlation_id'];
            this._resetSuccess = params['reset'] === 'ok';

            if (this._clientId) {
                // Hold the card behind a spinner until config resolves (bounded), so
                // it paints in the resolved language with no flash, and so the first
                // submit sees the right captcha/signup flags.
                this.configLoading = true;
                this.configTimeoutSub = timer(CONFIG_SPINNER_TIMEOUT_MS)
                    .pipe(takeUntilDestroyed(this.destroyRef))
                    .subscribe(() => this.configLoading = false);
                this.tenantConfigService.fetchConfig(this._clientId).pipe(
                    takeUntilDestroyed(this.destroyRef)
                ).subscribe({
                    next: config => {
                        this.signupEnabled = config.signup_enabled === true;
                        this.captchaRequired = config.captcha_required === true;
                        this.captchaConfig = config.captcha;
                        this.tenantI18nService.apply(config);
                        // Story 29.4 — fall back to the operator's default language when
                        // the visitor's browser language is unavailable (no stored choice).
                        this.languageService.applyEnvironmentDefault(config.default_language, Object.keys(config.i18n_overrides ?? {}));
                        this.configTimeoutSub?.unsubscribe();
                        this.configLoading = false;
                    },
                    error: () => {
                        this.signupEnabled = false;
                        this.captchaRequired = false;
                        this.configTimeoutSub?.unsubscribe();
                        this.configLoading = false;
                    },
                });

                // Epic 30: check whether the operator has published a custom sign-in
                // page for this client's environment. If so, redirect to /login where
                // serve-pages intercepts and returns the operator's full HTML document.
                // Runs in parallel with the tenant-config fetch so there's no added
                // latency beyond the slower of the two calls.
                this.customPageChecking = true;
                this.http.get<{ published: boolean }>(`${resolveApiBase()}/pages/sign-in?client_id=${this._clientId}`)
                    .pipe(takeUntilDestroyed(this.destroyRef))
                    .subscribe({
                        next: page => {
                            if (page.published) {
                                this.redirectToCustomPage();
                            } else {
                                this.customPageChecking = false;
                            }
                        },
                        error: () => { this.customPageChecking = false; },
                    });
            }
        });

        this.loginForm = new FormGroup({
            email: new FormControl('', [Validators.required, Validators.email]),
            password: new FormControl('', Validators.required),
            returnUrl: new FormControl(this._redirectUri, []),
            clientId: new FormControl(this._clientId, [])
        });
    }

    ngOnInit(): void {
        // If we arrived here with an `error` query param, the backend rejected
        // the session we last had — logged out, expired, or its user gone —
        // and already cleared the session cookie on that redirect. A silent
        // sign-in attempt now could only fail again, so the form shows.
        if (this._error) {
            return;
        }
        // Story 53.9 — silent sign-in rides the host-only session cookie: one
        // POST /login with no credentials, which the browser sends with the
        // cookie. 200 → the session is live, continue the flow; anything else
        // (401: no session) → the form stays, exactly once — no retry, no
        // error shown. Nothing here reads, stores or sends a session id.
        this.silentSignIn = this.loginService.login('', '', this._clientId, undefined, undefined, this._correlationId).subscribe({
            next: (response: LoginResponse) => {
                this.adoptCorrelation(response);
                this.postAuthRedirect();
            },
            error: () => { /* no live session — show the form */ }
        });
    }

    async login() {
        if (this.submitting) {
            return;
        }
        // Disable the button as soon as sign-in starts so it's clear something is
        // happening; re-enabled only on failure (a success navigates away).
        this.submitting = true;
        // The user typed credentials: whatever the in-flight silent attempt
        // answers, these win (53.9 code review).
        this.silentSignIn?.unsubscribe();
        this._error = null;
        this._emailNotVerified = false;
        this.resendSent = false;
        let captcha: CaptchaToken | undefined;
        if (this.captchaRequired) {
            // Story 54.4 — solved here, at submit, every time (never at load).
            this.captchaSolving = true;
            try {
                captcha = await this.captchaService.solve('login', this.clientId, this.captchaConfig);
            } catch {
                this.submitting = false;
                this._error = this.translocoService.translate('common.errors.captchaFailed');
                return;
            } finally {
                this.captchaSolving = false;
            }
        }
        this.loginService.login(this.email, this.password, this.clientId, captcha, this._deviceUserCode, this._correlationId).subscribe({
            next: (response: LoginResponse) => {
                this._error = null;
                this.adoptCorrelation(response);
                this.postAuthRedirect();
            },
            error: (error: HttpErrorResponse) => {
                this.submitting = false;
                // AC2: map the backend error CODE to a localized message — do NOT
                // surface error.errorDescription (a non-localizable English string).
                const key = ERROR_KEYS[error.error?.error] ?? 'login.errors.loginFailed';
                this._error = this.translocoService.translate(key);
                // Story 28.3 — surface the resend action only for the unverified case.
                this._emailNotVerified = error.error?.error === 'email_not_verified';
            }
        });
    }

    // Story 28.3 — re-issue + resend the verification email for the entered
    // address. The backend (/resend-verification) is anti-enumeration (always
    // 200), so we show the same confirmation regardless of outcome.
    resendVerification(): void {
        if (this.resendInProgress || !this.email) {
            return;
        }
        this.resendInProgress = true;
        this.emailVerificationService.resendVerification(this.email, this.clientId).subscribe({
            next: () => {
                this.resendInProgress = false;
                this.resendSent = true;
            },
            error: () => {
                this.resendInProgress = false;
                this.resendSent = true;
            },
        });
    }

    get email(): string {
        return this.loginForm.get('email')?.value;
    }

    get password(): string {
        return this.loginForm.get('password')?.value;
    }

    get clientId(): string {
        return this._clientId || '';
    }

    get error() {
        // The authorize flow redirects a stale/expired session back here with
        // ?error=session_invalid — that's an expected prompt to sign in again,
        // not a user-facing error, so it isn't surfaced. `_error` itself stays
        // set so ngOnInit still skips the silent sign-in attempt.
        return this._error === 'session_invalid' ? null : this._error;
    }

    get resetSuccess(): boolean {
        return this._resetSuccess;
    }

    get emailNotVerified(): boolean {
        return this._emailNotVerified;
    }

    // Extracted for testability — Karma can spy on this to verify the redirect
    // fires without actually navigating (window.location.replace is non-spyable
    // in jsdom). Guard on pathname prevents a redirect loop when the Angular
    // /login route (local dev) renders this same component.
    protected redirectToCustomPage(): void {
        if (this.currentPathname() !== '/login') {
            window.location.replace('/login' + window.location.search);
            return;
        }
        // Already AT /login. serve-pages was supposed to have returned the
        // operator's HTML before Angular ever loaded, so reaching this line
        // means it fell back to the SPA instead (a routing miss, an unpublish
        // that raced this fetch, or local dev where the Angular route renders
        // this component). Redirecting again would loop, so clear the flag and
        // render the default form.
        //
        // Without this the page HANGS: customPageChecking stays true and the
        // spinner never resolves — no console error, no pending request, so it
        // presents as a frozen page with nothing to see in DevTools. Observed
        // live on dev 2026-09-07, caused by a Traefik priority tie the gitops
        // side now fixes; this is the defence in depth, not the cure.
        this.customPageChecking = false;
    }

    // Seam: window.location.pathname is never '/login' under Karma, so without
    // this the already-at-/login branch above is untestable (the spec would
    // navigate the runner instead).
    protected currentPathname(): string {
        return window.location.pathname;
    }

    // Story 32.4 — the post-authentication redirect, shared by the silent-login
    // (ngOnInit) and form-submit (login) paths. When a device sign-in round-trip
    // is in progress (?device_user_code present) it returns to the /device page;
    // otherwise it builds the normal /authorize URL exactly as before. Extracted
    // so both paths branch identically and Karma can assert without navigating.
    protected postAuthRedirect(): void {
        if (this._deviceUserCode) {
            this.navigateTo(`/device?user_code=${encodeURIComponent(this._deviceUserCode)}`);
            return;
        }
        const correlation = this._correlationId ? `&correlation_id=${encodeURIComponent(this._correlationId)}` : '';
        // Story 53.9: no session_id — /authorize reads the host-only cookie.
        this.navigateTo(resolveApiBase() + `/authorize?audience=${this._audience}&state=${this._state}&scope=${this._scope}&client_id=${this._clientId}&response_type=code&redirect_uri=${this._redirectUri}&code_challenge=${this._codeChallenge}&code_challenge_method=S256&nonce=${this._nonce}${correlation}`);
    }

    // Story 49.6 — when the query carried no correlation id (a direct visit),
    // adopt the one /login minted and returned, so the rebuilt /authorize joins
    // the same flow instead of minting a second one.
    private adoptCorrelation(response: LoginResponse): void {
        if (!this._correlationId && response.correlation_id) {
            this._correlationId = response.correlation_id;
        }
    }

    // Seam: a full-page navigation cannot run under Karma; the spec captures
    // the URL instead of following it.
    protected navigateTo(url: string): void {
        window.location.href = url;
    }
}
